"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/client/api/axios";

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface FooterConfigData {
  id: number | null;
  columns: FooterColumn[];
  copyrightText: string | null;
  brandLabel: string | null;
  playStoreUrl: string | null;
  appStoreUrl: string | null;
  updatedAt: string | null;
}

const QUERY_KEY = ["footer-config"] as const;

async function fetchConfig(): Promise<FooterConfigData> {
  const res = await fetch("/api/footer-config", { credentials: "omit" });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error ?? "Failed to fetch footer config");
  return json?.data ?? json;
}

async function updateConfig(payload: {
  columns: FooterColumn[];
  copyrightText?: string | null;
  brandLabel?: string | null;
  playStoreUrl?: string | null;
  appStoreUrl?: string | null;
}) {
  const { data } = await api.put<{ success: boolean; data: FooterConfigData }>("/footer-config", payload);
  return data.data ?? data;
}

export function useFooterConfigQuery() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchConfig,
  });
}

export function useUpdateFooterConfigMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateConfig,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function getFooterConfigError(error: unknown): string | undefined {
  const err = error as {
    response?: {
      data?: { error?: string; message?: string; details?: Array<{ message?: string }> };
      status?: number;
    };
  };
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (data?.details?.[0]?.message) return data.details[0].message;
  if (err?.response?.status === 401) return "Please sign in to continue.";
  return undefined;
}
